/**
 * iter365 — Rattacher une conversation WhatsApp à un client : le CRM propose
 * le client le plus probable (même numéro, nom WhatsApp), on confirme ou on
 * corrige via une recherche libre.
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, UserCheck, Unlink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const RattacherClientDialog = ({ open, waId, actuelLabel, onClose, onDone }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async (recherche) => {
    setChargement(true);
    try {
      const { data } = await api.get(`/whatsapp/conversations/${waId}/clients-probables`,
        { params: recherche ? { q: recherche } : {} });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setChargement(false);
    }
  }, [waId]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    charger("");
  }, [open, charger]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => charger(q.trim().length >= 2 ? q.trim() : ""), 300);
    return () => clearTimeout(t);
  }, [open, q, charger]);

  const rattacher = async (clientId, label) => {
    setBusy(true);
    try {
      await api.post(`/whatsapp/conversations/${waId}/rattacher`, { client_id: clientId });
      toast.success(clientId ? `Conversation rattachée à ${label}` : "Conversation détachée");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rattachement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="whatsapp-rattacher-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-[#52a675]" />
            {actuelLabel ? "Changer de client" : "Rattacher à un client"}
          </DialogTitle>
        </DialogHeader>

        {actuelLabel && (
          <div className="text-xs text-gray-600" data-testid="whatsapp-rattache-actuel">
            Actuellement rattachée à <strong>{actuelLabel}</strong>
          </div>
        )}

        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Chercher un client par nom, téléphone ou e-mail…"
          className="h-11" data-testid="whatsapp-rattacher-recherche"
        />

        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {chargement && <div className="text-xs text-gray-400 text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
          {!chargement && items.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-4" data-testid="whatsapp-rattacher-vide">
              Aucun client probable — cherchez par nom ou téléphone.
            </div>
          )}
          {items.map((c) => (
            <button
              key={c.id} disabled={busy}
              onClick={() => rattacher(c.id, c.label)}
              className="w-full text-left p-2.5 rounded-xl border border-gray-200 hover:border-[#52a675] transition-colors flex items-center gap-2"
              data-testid={`whatsapp-rattacher-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.label}</div>
                <div className="text-[11px] text-gray-500">{c.tel}</div>
              </div>
              <Badge
                variant="outline"
                className={c.score >= 100
                  ? "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30 text-[10px]"
                  : "bg-gray-50 text-gray-600 border-gray-200 text-[10px]"}
              >
                {c.motif}
              </Badge>
            </button>
          ))}
        </div>

        <DialogFooter className="gap-2">
          {actuelLabel && (
            <Button
              variant="outline" disabled={busy} onClick={() => rattacher(null, null)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="whatsapp-detacher"
            >
              <Unlink className="w-4 h-4 mr-1.5" /> Détacher
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="whatsapp-rattacher-annuler">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RattacherClientDialog;
